import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import * as deepClone from 'lodash.clonedeep';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Inject, Injectable } from '@angular/core';
import { TokenManagementService } from '../token-management/token-management.service';
import { I18nHelperService } from '@xref/ngx';


declare global {
  interface Window {
    DD_RUM: any;
    DD_LOGS: any;
  }
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    @Inject(TokenManagementService) private tokenManagement: TokenManagementService,
    @Inject(I18nHelperService) private i18nHelper: I18nHelperService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    try {
      // This is being called for every call and is creating large number of events to Sentry
      // Uncomment while debugging
      // window.DD_LOGS.logger.debug(`${this.cleanRequestObject(deepClone(req))})`;
    } catch (e) { }

    // Will refresh token if past half way of expiry
    this.tokenManagement.refreshToken();

    let modifiedReq = req;
    const bearerBackup = req.headers.get('X-OAuth-Bearer-Token');
    const isOAuthEndpoint = req.url.includes('/v1/jwt/obtain/oauth');

    if (isOAuthEndpoint) {
      // For OAuth endpoint, ensure Bearer token is sent
      if (bearerBackup) {
        const headers = req.headers
          .delete('X-OAuth-Bearer-Token')
          .set('Authorization', bearerBackup);
        modifiedReq = req.clone({headers});
      }
    }

    const reqWithLocale = this.addCurrentLocale(modifiedReq);

    return next.handle(reqWithLocale).pipe(catchError(this.handleError));
  }

  // Add locale as custom header, used by API to set email languages
  addCurrentLocale(req: HttpRequest<any>): HttpRequest<any> {
    if (req.url.startsWith(environment.API)) {
      let locale: any = this.i18nHelper.locale;
      locale = locale.split('-');
      locale[1] = locale[1].toUpperCase();
      locale = locale.join('-');
      return req.clone({
        setHeaders: { 'x-lang-locale': locale }
      });
    } else {
      return req;
    }
  }

  private handleError(error: Response | any, caught: any): Observable<any> {
    try {
      if (environment.ddg_enabled) window.DD_LOGS.logger.error(error);
    } catch (e) { }

    return throwError(error);
  }

  // Remove unwanted information from request object that we don't want to track
  private cleanRequestObject(request: HttpRequest<any>): HttpRequest<any> {
    if (request['body']) {
      // If request object contains email remove it
      if (request['body'].hasOwnProperty('email') && request['body'].email.length > 0) request['body'].email = 'email removed';
    }

    // Remove JWT from request object
    if (request.headers.get('X-JWT'))
      request = request.clone({ setHeaders: { 'X-JWT': '***' } });

    return request;
  }

}
